import {
    GTMEcommerce,
    GTMEcommerceEvent,
    GTMEcommerceItem,
    GTMEventName,
    TrackingCommerce,
    TrackingItem,
} from '../types';

type GTMEcommerceEventRootFields = Record<string, unknown> & {
    ecommerce?: never;
    event?: never;
};

type GTMEventRootFields = Record<string, unknown> & {
    event?: never;
};

const omitUndefined = <Data extends Record<string, unknown>>(data: Data): Data =>
    Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined)) as Data;

export const createGTMEvent = <RootFields extends GTMEventRootFields>(
    event: GTMEventName,
    rootFields: RootFields = {} as RootFields,
): { event: GTMEventName } & RootFields => ({
    event,
    ...omitUndefined(rootFields),
});

export const mapTrackingItemToGTMItem = (item: TrackingItem): GTMEcommerceItem =>
    omitUndefined({
        item_type: item.type,
        item_id: item.id,
        item_name: item.name,
        item_brand: item.brand,
        item_variant: item.variant,
        item_category: item.category,
        item_list_id: item.listId,
        item_list_name: item.listName,
        index: item.index,
        original_price: item.originalPrice,
        price: item.price,
        discount: item.discount,
        quantity: item.quantity,
        commitment_type: item.commitmentType,
        service_type: item.serviceType,
        currency: item.currency,
        funnel_category: item.funnelCategory,
        funnel_variant: item.funnelVariant,
        previous_plan: item.previousPlan,
        previous_operator: item.previousOperator,
    });

export const mapTrackingCommerceToGTMEcommerce = (commerce: TrackingCommerce): GTMEcommerce =>
    omitUndefined({
        order_type: commerce.orderType,
        segment: commerce.segment,
        currency: commerce.currency,
        value: commerce.value,
        item_list_name: commerce.itemListName,
        item_category: commerce.itemCategory,
        gift_included: commerce.giftIncluded,
        coupon: commerce.coupon,
        payment_type: commerce.paymentType,
        payment_method: commerce.paymentMethod,
        sign_method: commerce.signMethod,
        bank: commerce.bank,
        transaction_id: commerce.transactionId,
        subscriber_id: commerce.subscriberId,
        shipping_tier: commerce.shippingTier,
        leasing_monthly_value: commerce.leasingMonthlyValue,
        instant_value: commerce.instantValue,
        leasing_period: commerce.leasingPeriod,
        search_text: commerce.searchText,
        suggestion_text: commerce.suggestionText,
        click_name: commerce.clickName,
        items: commerce.items?.map(mapTrackingItemToGTMItem),
        ...commerce.additionalProperties,
    });

export const createGTMEcommerceEvent = (
    event: GTMEventName,
    commerce: TrackingCommerce,
    rootFields: GTMEcommerceEventRootFields = {},
): GTMEcommerceEvent =>
    createGTMEvent(event, {
        ...rootFields,
        ecommerce: mapTrackingCommerceToGTMEcommerce(commerce),
    });
